/**
 * ═══ A BATCH PART'S LAST ANSWER, KEPT FOR "UNCHANGED" (#2481) ═══
 *
 * A read the phone sends on its own is kept by the browser, which asks again with the answer's fingerprint and draws its
 * own copy on a 304 (src/server/unchangedAnswers.ts). A read inside a batch never reaches the browser's copy, so the
 * batch (batchedReads.ts) keeps the last answer of every part that came with a fingerprint here, names those
 * fingerprints when the same address is asked again, and draws the copy on a part answered "unchanged".
 *
 * Kept per signed-in person (the batch's own headers are part of the key), at most `MOST` answers and `MOST_CHARS` of
 * text, the newest kept. Also kept for the tab's life across a reload (sessionStorage, written when the page is hidden),
 * so a trader opened again after a reload is answered "unchanged" too; nothing is kept past the tab.
 *
 * `localStorage["nmo.break.2481"] = "no_unchanged"` keeps nothing (the check's deliberate fault).
 */
export interface KeptPart {
  readonly tag: string;
  readonly status: number;
  readonly headers: Record<string, string>;
  readonly body: string;
}

const MOST = 120;
const MOST_CHARS = 4_000_000;
/** What survives a reload: less, so writing it when the page hides stays quick. */
const SAVED_CHARS = 2_500_000;
const STORE_KEY = "genie.keptParts.v1";

const kept = new Map<string, KeptPart>();
let chars = 0;
let loaded = false;

const broken = (): boolean => { try { return (localStorage.getItem("nmo.break.2481") ?? "").split(",").includes("no_unchanged"); } catch { return false; } };

function load(): void {
  if (loaded) return;
  loaded = true;
  /** Before anything is read back: a tab with nothing saved yet still saves what it keeps from now on. */
  if (typeof addEventListener === "function") {
    addEventListener("pagehide", save);
    addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") save(); });
  }
  try {
    const said = sessionStorage.getItem(STORE_KEY);
    if (said === null) return;
    const list = JSON.parse(said) as Array<[string, KeptPart]>;
    for (const [key, part] of list) {
      if (typeof key === "string" && part && typeof part.tag === "string" && typeof part.body === "string") put(key, part);
    }
  } catch { /* nothing kept */ }
}

function save(): void {
  try {
    const out: Array<[string, KeptPart]> = [];
    let total = 0;
    /** Newest first: the Map's last entries are the newest. */
    for (const [key, part] of [...kept].reverse()) {
      if (total + part.body.length > SAVED_CHARS) continue;
      total += part.body.length;
      out.push([key, part]);
    }
    sessionStorage.setItem(STORE_KEY, JSON.stringify(out.reverse()));
  } catch { /* full or refused: nothing survives the reload */ }
}

function put(key: string, part: KeptPart): void {
  const old = kept.get(key);
  if (old) { chars -= old.body.length; kept.delete(key); }
  if (part.body.length > MOST_CHARS / 4) return;
  kept.set(key, part);
  chars += part.body.length;
  for (const [oldest, one] of kept) {
    if (kept.size <= MOST && chars <= MOST_CHARS) break;
    kept.delete(oldest);
    chars -= one.body.length;
  }
}

/** The kept answer for this read, if any. */
export function keptPart(key: string): KeptPart | null {
  if (broken()) return null;
  load();
  const one = kept.get(key);
  if (!one) return null;
  /** Used again: it is the newest now. */
  kept.delete(key);
  kept.set(key, one);
  return one;
}

/** Keeps a part's answer when it came with a fingerprint (and only then). */
export function keepPart(key: string, status: number, headers: Record<string, string>, body: Uint8Array): void {
  if (broken() || status !== 200) return;
  const tag = headers.etag;
  if (typeof tag !== "string" || tag === "") return;
  load();
  let text: string;
  try { text = new TextDecoder("utf-8", { fatal: true }).decode(body); } catch { return; }
  put(key, { tag, status, headers, body: text });
}
